/**
 * TT-02 — small fetch helper for the `time-tracking/v1` namespace.
 *
 * Mirrors `projectsFetch.ts`: the surface is TENANT-SCOPED, so every call sends
 * `X-Tenant-ID` alongside the bearer token. (`supportFetch` deliberately does
 * not — that surface is platform-admin and cross-tenant. This one is neither.)
 *
 * Base URL comes from `getTimeTrackingBaseUrl()` so `DEV_API_MODE`
 * (realbe / devapi-*) routes requests correctly. Errors carry the parsed JSON
 * body so callers can read the BE envelopes — in particular the 409
 * `{ code: "locked" }` that every mutation on a locked tracking answers with.
 */
import { getTimeTrackingBaseUrl, useAuthStore } from "@aeriosiq/auth-client";

export interface TimeTrackingFetchError extends Error {
  status: number;
  body: unknown;
}

interface TimeTrackingFetchOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

function authHeaders(): Headers {
  const session = useAuthStore.getState().session;
  const tenant = useAuthStore.getState().tenant;
  const headers = new Headers();
  if (session?.accessToken) {
    headers.set("Authorization", `Bearer ${session.accessToken}`);
  }
  if (tenant?.tenantSlug) {
    headers.set("X-Tenant-ID", tenant.tenantSlug);
  }
  return headers;
}

export async function timeTrackingFetch<T>(
  path: string,
  opts: TimeTrackingFetchOptions = {},
): Promise<T> {
  const headers = authHeaders();
  let bodyInit: BodyInit | undefined;
  if (opts.body !== undefined) {
    bodyInit = JSON.stringify(opts.body);
    headers.set("Content-Type", "application/json");
  }
  const init: RequestInit = {
    method: opts.method ?? "GET",
    headers,
  };
  if (bodyInit !== undefined) {
    init.body = bodyInit;
  }
  const res = await fetch(`${getTimeTrackingBaseUrl()}${path}`, init);
  if (!res.ok) {
    let parsedBody: unknown = null;
    try {
      parsedBody = await res.json();
    } catch {
      // Non-JSON error body — status still surfaces.
    }
    const err = new Error(
      `time-tracking fetch failed: ${path} → ${String(res.status)}`,
    ) as TimeTrackingFetchError;
    err.status = res.status;
    err.body = parsedBody;
    throw err;
  }
  if (res.status === 204) {
    return undefined as unknown as T;
  }
  const text = await res.text();
  if (text === "") return undefined as unknown as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    return undefined as unknown as T;
  }
}

/**
 * `true` when the BE refused a mutation because the tracking is locked.
 *
 * This is NOT an error condition to shout about — it means the back office
 * raised an invoice while the row was on the pilot's screen. The UI explains
 * and refreshes rather than showing a failure (TT-02 §3).
 */
export function isLockedConflict(error: unknown): boolean {
  const e = error as TimeTrackingFetchError | undefined;
  if (e?.status !== 409) return false;
  const body = e.body as { code?: string } | null;
  return body?.code === "locked";
}
